/**
 * AAC Prediction Engine (Complete Implementation)
 * Implements N-Gram Backoff (Trigram -> Bigram -> Unigram),
 * prefix search, phrase-pair detection,
 * and Hybrid Cloud/Local prediction.
 */

import {
  detectLanguage as detectLang,
  searchVocabulary,
  getVocabulary,
  type SupportedLanguage,
} from "./vocabulary";
import type { UserWordLearning, LearnedWord, LearnedBigram } from "./aacLearningSync";
import { seedDictionary } from "./aacDictionary";
import { supabase } from "@/integrations/supabase/client";

// ==================== Types ====================

export interface WordEntry {
  word: string;
  frequency: number;
  lastUsed: number;
  language: SupportedLanguage;
  isNextWord?: boolean;
  isPhrasePair?: boolean;
  source?: "local" | "ai";
}

export interface PhraseEntry {
  phrase: string;
  acronym: string;
  frequency: number;
  lastUsed: number;
  language: SupportedLanguage;
}

// ==================== Constants ====================

const STORAGE_KEY_WORDS = "aac_learned_words_v3";
const STORAGE_KEY_BIGRAMS = "aac_bigrams_v3";
const STORAGE_KEY_PHRASES = "aac_phrases_v3";
const STORAGE_KEY_RECENT = "aac_recent_words_v3";
const STORAGE_KEY_PHRASE_PAIRS = "aac_phrase_pairs_v3";
const RECENCY_BOOST_WINDOW = 5 * 60 * 1000;
const MAX_SUGGESTIONS = 6;
const PHRASE_PAIR_THRESHOLD = 30;

// ==================== Helpers ====================

function detectLanguage(text: string): SupportedLanguage {
  return detectLang(text);
}

// ==================== Core Class ====================

class AACPredictionEngine {
  private learnedWords: Map<string, WordEntry> = new Map();
  private bigrams: Record<string, Record<string, number>> = {};
  private phrases: Map<string, PhraseEntry> = new Map();
  private recentWords: { word: string; timestamp: number }[] = [];
  private phrasePairs: Record<string, Record<string, number>> = {};
  private onChangeCallback: (() => void) | null = null;

  constructor() {
    this.loadFromStorage();
  }

  // ==================== Storage ====================

  private loadFromStorage(): void {
    try {
      const wordsJson = localStorage.getItem(STORAGE_KEY_WORDS);
      if (wordsJson) {
        const arr: WordEntry[] = JSON.parse(wordsJson);
        arr.forEach((w) => this.learnedWords.set(w.word, w));
      }

      const bigramsJson = localStorage.getItem(STORAGE_KEY_BIGRAMS);
      if (bigramsJson) {
        this.bigrams = JSON.parse(bigramsJson);
      }

      const phrasesJson = localStorage.getItem(STORAGE_KEY_PHRASES);
      if (phrasesJson) {
        const arr: PhraseEntry[] = JSON.parse(phrasesJson);
        arr.forEach((p) => this.phrases.set(p.acronym + ":" + p.phrase, p));
      }

      const recentJson = localStorage.getItem(STORAGE_KEY_RECENT);
      if (recentJson) {
        this.recentWords = JSON.parse(recentJson);
      }

      const pairsJson = localStorage.getItem(STORAGE_KEY_PHRASE_PAIRS);
      if (pairsJson) {
        this.phrasePairs = JSON.parse(pairsJson);
      }
    } catch (e) {
      console.error("[PredictionEngine] Failed to load from storage:", e);
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(
        STORAGE_KEY_WORDS,
        JSON.stringify(Array.from(this.learnedWords.values()))
      );
      localStorage.setItem(STORAGE_KEY_BIGRAMS, JSON.stringify(this.bigrams));
      localStorage.setItem(
        STORAGE_KEY_PHRASES,
        JSON.stringify(Array.from(this.phrases.values()))
      );
      localStorage.setItem(STORAGE_KEY_RECENT, JSON.stringify(this.recentWords));
      localStorage.setItem(
        STORAGE_KEY_PHRASE_PAIRS,
        JSON.stringify(this.phrasePairs)
      );
    } catch (e) {
      console.error("[PredictionEngine] Failed to save to storage:", e);
    }

    // Trigger cloud sync callback
    if (this.onChangeCallback) {
      this.onChangeCallback();
    }
  }

  // ==================== Cloud Sync ====================

  setOnChangeCallback(cb: (() => void) | null): void {
    this.onChangeCallback = cb;
  }

  getLearnedData(): UserWordLearning {
    return {
      learned_words: Array.from(this.learnedWords.values()).map((w) => ({
        word: w.word,
        frequency: w.frequency,
        lastUsed: w.lastUsed,
        language: w.language as "he" | "en" | "ar" | "ru",
      })),
      learned_bigrams: this.bigrams as LearnedBigram,
      recent_words: this.recentWords,
    };
  }

  setCloudData(data: UserWordLearning): void {
    // Merge cloud learned words with local
    if (data.learned_words) {
      for (const w of data.learned_words) {
        const existing = this.learnedWords.get(w.word);
        if (!existing || w.frequency > existing.frequency) {
          this.learnedWords.set(w.word, {
            word: w.word,
            frequency: w.frequency,
            lastUsed: w.lastUsed,
            language: w.language,
          });
        }
      }
    }

    // Merge bigrams
    if (data.learned_bigrams) {
      for (const [prev, nextWords] of Object.entries(data.learned_bigrams)) {
        if (!this.bigrams[prev]) this.bigrams[prev] = {};
        for (const [next, freq] of Object.entries(nextWords)) {
          this.bigrams[prev][next] = Math.max(
            this.bigrams[prev][next] || 0,
            freq
          );
        }
      }
    }

    // Merge recent words
    if (data.recent_words) {
      const recentMap = new Map<string, number>();
      for (const r of this.recentWords) recentMap.set(r.word, r.timestamp);
      for (const r of data.recent_words) {
        const existing = recentMap.get(r.word);
        if (!existing || r.timestamp > existing) {
          recentMap.set(r.word, r.timestamp);
        }
      }
      this.recentWords = Array.from(recentMap.entries())
        .map(([word, timestamp]) => ({ word, timestamp }))
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 50);
    }

    this.saveToStorage();
  }

  // ==================== Main Search ====================

  /**
   * Main search with N-Gram Backoff (Trigram -> Bigram -> Unigram)
   */
  searchStandard(input: string): WordEntry[] {
    if (!input || !input.trim()) return [];

    const trimmed = input.trim();
    const isEndingInSpace = input.endsWith(" ");
    const lang = detectLanguage(trimmed);

    if (!isEndingInSpace && trimmed.length > 0) {
      // User is typing a word – do prefix search
      return this.prefixSearch(trimmed, lang);
    }

    // User just finished a word – predict next word with backoff
    return this.predictNextWord(trimmed, lang);
  }

  /**
   * Get suggestions (alias for searchStandard for the new API)
   */
  getSuggestions(input: string): WordEntry[] {
    return this.searchStandard(input);
  }

  /**
   * Prefix search: find words starting with the last partial word
   */
  private prefixSearch(input: string, lang: SupportedLanguage): WordEntry[] {
    const words = input.split(/\s+/);
    const lastWord = words[words.length - 1];
    if (!lastWord) return [];

    const results: WordEntry[] = [];
    const seen = new Set<string>();

    // 1. Check learned words first (personal vocabulary)
    for (const entry of this.learnedWords.values()) {
      if (
        entry.language === lang &&
        entry.word.toLowerCase().startsWith(lastWord.toLowerCase()) &&
        entry.word.toLowerCase() !== lastWord.toLowerCase()
      ) {
        if (!seen.has(entry.word)) {
          seen.add(entry.word);
          const recencyBoost =
            Date.now() - entry.lastUsed < RECENCY_BOOST_WINDOW ? 50 : 0;
          results.push({
            ...entry,
            frequency: entry.frequency + recencyBoost,
            isNextWord: false,
          });
        }
      }
    }

    // 2. Fill from vocabulary files
    if (results.length < MAX_SUGGESTIONS) {
      const vocabResults = searchVocabulary(
        lastWord,
        lang,
        MAX_SUGGESTIONS - results.length + 5
      );
      for (const word of vocabResults) {
        if (!seen.has(word)) {
          seen.add(word);
          results.push({
            word,
            frequency: 1,
            lastUsed: 0,
            language: lang,
            isNextWord: false,
          });
        }
        if (results.length >= MAX_SUGGESTIONS) break;
      }
    }

    return results
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, MAX_SUGGESTIONS);
  }

  /**
   * Predict next word using Trigram -> Bigram -> Unigram backoff
   */
  private predictNextWord(
    input: string,
    lang: SupportedLanguage
  ): WordEntry[] {
    const words = input.split(/\s+/).filter(Boolean);
    const predictions = new Map<string, WordEntry>();

    // 1. Check phrase pairs (e.g. "מרגיש" -> "שמח")
    if (words.length >= 1) {
      const lastWord = words[words.length - 1].toLowerCase();
      const pairNext = this.phrasePairs[lastWord];
      if (pairNext) {
        const sortedPairs = Object.entries(pairNext)
          .filter(([, freq]) => freq >= PHRASE_PAIR_THRESHOLD)
          .sort(([, a], [, b]) => b - a);
        for (const [word, freq] of sortedPairs) {
          if (!predictions.has(word)) {
            predictions.set(word, {
              word,
              frequency: freq * 3,
              lastUsed: Date.now(),
              language: lang,
              isNextWord: true,
              isPhrasePair: true,
            });
          }
          if (predictions.size >= MAX_SUGGESTIONS) break;
        }
      }
    }

    // 2. Try Trigram (last 2 words)
    if (words.length >= 2) {
      const context =
        `${words[words.length - 2]} ${words[words.length - 1]}`.toLowerCase();
      this.addBigramPredictions(context, predictions, lang, 2.0);
    }

    // 3. Try Bigram (last 1 word)
    if (words.length >= 1) {
      const context = words[words.length - 1].toLowerCase();
      this.addBigramPredictions(context, predictions, lang, 1.5);
    }

    // 4. Unigram fallback (global frequency from learned + vocabulary)
    if (predictions.size < MAX_SUGGESTIONS) {
      // Learned words
      const learnedByFreq = Array.from(this.learnedWords.values())
        .filter((w) => w.language === lang)
        .sort((a, b) => b.frequency - a.frequency);
      for (const entry of learnedByFreq) {
        if (!predictions.has(entry.word)) {
          predictions.set(entry.word, {
            ...entry,
            isNextWord: true,
          });
        }
        if (predictions.size >= MAX_SUGGESTIONS) break;
      }
    }

    // 5. If still not enough, pull from vocabulary
    if (predictions.size < MAX_SUGGESTIONS) {
      const vocabWords = getVocabulary(lang).slice(
        0,
        MAX_SUGGESTIONS * 3
      );
      for (const word of vocabWords) {
        if (!predictions.has(word)) {
          predictions.set(word, {
            word,
            frequency: 1,
            lastUsed: 0,
            language: lang,
            isNextWord: true,
          });
        }
        if (predictions.size >= MAX_SUGGESTIONS) break;
      }
    }

    return Array.from(predictions.values())
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, MAX_SUGGESTIONS);
  }

  private addBigramPredictions(
    contextKey: string,
    map: Map<string, WordEntry>,
    lang: SupportedLanguage,
    boost: number
  ): void {
    const nextWords = this.bigrams[contextKey];
    if (!nextWords) return;

    for (const [word, freq] of Object.entries(nextWords)) {
      if (!map.has(word)) {
        map.set(word, {
          word,
          frequency: freq * boost,
          lastUsed: Date.now(),
          language: lang,
          isNextWord: true,
          source: "local",
        });
      }
    }
  }

  // ==================== Learning ====================

  /**
   * Called when user selects a word suggestion
   */
  onWordSelected(word: string, fullPreviousText: string): void {
    const lang = detectLanguage(word);

    // Update learned words (unigram)
    const existing = this.learnedWords.get(word);
    if (existing) {
      existing.frequency += 5;
      existing.lastUsed = Date.now();
    } else {
      this.learnedWords.set(word, {
        word,
        frequency: 5,
        lastUsed: Date.now(),
        language: lang,
      });
    }

    // Update recent words
    this.recentWords = this.recentWords.filter((r) => r.word !== word);
    this.recentWords.unshift({ word, timestamp: Date.now() });
    if (this.recentWords.length > 50) this.recentWords = this.recentWords.slice(0, 50);

    const prevWords = fullPreviousText.trim().split(/\s+/).filter(Boolean);

    // Learn Bigram (prev 1 word -> current)
    if (prevWords.length >= 1) {
      const prev = prevWords[prevWords.length - 1].toLowerCase();
      this.updateBigram(prev, word.toLowerCase());

      // Track phrase pair
      if (!this.phrasePairs[prev]) this.phrasePairs[prev] = {};
      this.phrasePairs[prev][word.toLowerCase()] =
        (this.phrasePairs[prev][word.toLowerCase()] || 0) + 1;
    }

    // Learn Trigram (prev 2 words -> current)
    if (prevWords.length >= 2) {
      const prevTwo =
        `${prevWords[prevWords.length - 2]} ${prevWords[prevWords.length - 1]}`.toLowerCase();
      this.updateBigram(prevTwo, word.toLowerCase());
    }

    this.saveToStorage();
  }

  /**
   * Called when user selects a phrase (acronym mode)
   */
  onPhraseSelected(phrase: string, acronym: string): void {
    const key = acronym + ":" + phrase;
    const existing = this.phrases.get(key);
    if (existing) {
      existing.frequency += 5;
      existing.lastUsed = Date.now();
    } else {
      this.phrases.set(key, {
        phrase,
        acronym,
        frequency: 5,
        lastUsed: Date.now(),
        language: detectLanguage(phrase),
      });
    }
    this.saveToStorage();
  }

  private updateBigram(context: string, nextWord: string): void {
    if (!this.bigrams[context]) this.bigrams[context] = {};
    this.bigrams[context][nextWord] =
      (this.bigrams[context][nextWord] || 0) + 1;
  }

  // ==================== Acronym Search ====================

  searchAcronym(query: string): PhraseEntry[] {
    if (!query.trim()) return [];
    const lower = query.toLowerCase().replace(/\s/g, "");
    const lang = detectLanguage(query);

    const results: PhraseEntry[] = [];
    for (const entry of this.phrases.values()) {
      if (entry.language === lang && entry.acronym.startsWith(lower)) {
        results.push(entry);
      }
    }

    // Also search seed dictionary
    for (const entry of seedDictionary) {
      if (
        entry.language === lang &&
        entry.acronym.startsWith(lower)
      ) {
        results.push({
          phrase: entry.text,
          acronym: entry.acronym,
          frequency: entry.frequency,
          lastUsed: 0,
          language: entry.language as SupportedLanguage,
        });
      }
    }

    return results
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, MAX_SUGGESTIONS);
  }

  // ==================== AI Predictions (Pro Feature) ====================

  /**
   * Fetches context-aware predictions from Lovable AI Gateway
   * Should be called debounced (e.g., after user pauses typing for 600ms)
   */
  async fetchSmartPredictions(fullText: string): Promise<WordEntry[]> {
    if (!fullText || fullText.trim().split(/\s+/).length < 3) return [];

    try {
      const { data, error } = await supabase.functions.invoke('predict-next-word', {
        body: { text: fullText },
      });

      if (error) return [];

      const suggestions: string[] = data?.suggestions || [];
      const lang = detectLanguage(fullText);

      return suggestions.map((word: string) => ({
        word,
        frequency: 999,
        lastUsed: Date.now(),
        language: lang,
        isNextWord: true,
        source: "ai" as const,
      }));
    } catch {
      return [];
    }
  }
}

// ==================== Singleton ====================

let engineInstance: AACPredictionEngine | null = null;

export function getPredictionEngine(): AACPredictionEngine {
  if (!engineInstance) {
    engineInstance = new AACPredictionEngine();
  }
  return engineInstance;
}
